// src/pages/Inventory.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Grid,
  Box,
  Paper,
  IconButton,
  Stack,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  query,
  where,
  getDoc,
  doc,
  setDoc,
  getDocs,
  collection,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore, storage } from "../firebase";
import { Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";

const Inventory = () => {
  const auth = getAuth();
  const [user, loadingAuth] = useAuthState(auth);
  const shopOwnerId = user ? user.uid : null;

  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    plateNr: "",
    brand: "",
    model: "",
    description: "",
    dailyRate: "",
    oneWeekRate: "",
    twoWeeksRate: "",
    oneMonthRate: "",
    extraHoursRate: "",
    deposit: "",
    imageUrl: "",
    extras: [],
    engineSize: "",
    fuelType: "",
    seats: 2,
    transmission: "Automatic",
    type: "Motorbike",
  });
  const [imageFile, setImageFile] = useState(null);
  const [extraName, setExtraName] = useState("");
  const [extraPrice, setExtraPrice] = useState("");
  const [shopSlug, setShopSlug] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  const fetchShopSlug = async (userId) => {
    const ownerDocRef = doc(firestore, "users", userId);
    const ownerSnap = await getDoc(ownerDocRef);

    if (ownerSnap.exists()) {
      return ownerSnap.data().shopId; // Assuming `shopId` is the slug
    } else {
      console.error("No shop found for this user in users.");
      return null;
    }
  };

  useEffect(() => {
    if (user) {
      fetchShopSlug(user.uid).then((slug) => {
        if (slug) {
          setShopSlug(slug);
        }
      });
    }
  }, [user]);

  // Fetch vehicle inventory from Firestore once shopId is available
  useEffect(() => {
    if (!shopSlug) return;
    const fetchVehicles = async () => {
      try {
        setLoadingVehicles(true); // start loading
        const querySnapshot = await getDocs(
          collection(firestore, "rentalShops", shopSlug, "vehicles")
        );
        const vehicleList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVehicles(vehicleList);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoadingVehicles(false); // done loading
      }
    };

    fetchVehicles();
  }, [shopSlug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVehicle((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  // Extra handling
  const handleAddExtra = () => {
    if (!extraName) return;
    const id = extraName.toLowerCase().replace(/\s+/g, "_");
    const extra = {
      id,
      name: extraName,
      price: extraPrice === "" ? 0 : Number(extraPrice),
      optional: true,
    };
    setNewVehicle((prev) => ({
      ...prev,
      extras: [...prev.extras, extra],
    }));
    setExtraName("");
    setExtraPrice("");
  };

  const handleRemoveExtra = (index) => {
    setNewVehicle((prev) => ({
      ...prev,
      extras: prev.extras.filter((_, idx) => idx !== index),
    }));
  };

  // Handle form submission to add a new vehicle
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!shopOwnerId) return alert("User not authenticated");
    setLoading(true);

    try {
      // Generate custom ID: MODEL-PLATENR-XXXX
      const randomSuffix = Math.random()
        .toString(36)
        .substring(2, 6)
        .toUpperCase();
      const formattedModel = newVehicle.model.toUpperCase().replace(/\s+/g, "");
      const formattedPlate = newVehicle.plateNr
        .toUpperCase()
        .replace(/\s+/g, "");
      const customId = `${formattedModel}-${formattedPlate}-${randomSuffix}`;

      let uploadedImageUrl = "";
      let uploadedImagePath = "";
      if (imageFile) {
        const safeFileName = imageFile.name.replace(/\s+/g, "_");
        uploadedImagePath = `vehicleImages/${shopSlug}/${customId}/${safeFileName}`;
        const storageRef = ref(storage, uploadedImagePath);
        await uploadBytes(storageRef, imageFile);
        uploadedImageUrl = await getDownloadURL(storageRef);
      }

      const name = `${newVehicle.brand} ${newVehicle.plateNr}`;
      const now = new Date().toISOString();

      const vehicleData = {
        name,
        type: newVehicle.type,
        plateNr: newVehicle.plateNr,
        brand: newVehicle.brand,
        model: newVehicle.model,
        brandModel: `${newVehicle.brand} ${newVehicle.model}`,
        description: newVehicle.description,
        ownerId: shopOwnerId,
        updatedAt: now,
        pricing: {
          daily: Number(newVehicle.dailyRate),
          oneWeek: newVehicle.oneWeekRate
            ? Number(newVehicle.oneWeekRate)
            : null,
          twoWeeks: newVehicle.twoWeeksRate
            ? Number(newVehicle.twoWeeksRate)
            : null,
          oneMonth: newVehicle.oneMonthRate
            ? Number(newVehicle.oneMonthRate)
            : null,
          extraHours: Number(newVehicle.extraHoursRate),
          currency: "PHP",
        },
        deposit: Number(newVehicle.deposit),
        availability: {
          defaultBlockedDays: [],
          futureMaintenanceBlocks: [],
        },
        extras: newVehicle.extras,
        images: uploadedImageUrl
          ? [
              {
                url: uploadedImageUrl,
                path: uploadedImagePath,
                primary: true,
                alt: `${newVehicle.brand} ${newVehicle.model}`,
              },
            ]
          : [],
        features: {
          engineSize: newVehicle.engineSize,
          transmission: newVehicle.transmission,
          seats: Number(newVehicle.seats),
          fuelType: newVehicle.fuelType,
        },
        flags: {
          active: true,
          featured: false,
          underMaintenance: false,
        },
      };

      const vehicleRef = doc(
        firestore,
        "rentalShops",
        shopSlug,
        "vehicles",
        customId
      );

      await setDoc(vehicleRef, vehicleData);

      setVehicles((prev) => [...prev, { id: customId, ...vehicleData }]);
      setNewVehicle({
        plateNr: "",
        brand: "",
        model: "",
        description: "",
        dailyRate: "",
        oneWeekRate: "",
        twoWeeksRate: "",
        oneMonthRate: "",
        extraHoursRate: "",
        deposit: "",
        imageUrl: "",
        extras: [],
        engineSize: "",
        fuelType: "",
        seats: 2,
        transmission: "Automatic",
        type: "Motorbike",
      });
      setImageFile(null);
      setShowForm(false);
      setLoading(false);
    } catch (error) {
      console.error("Error adding vehicle:", error);
      alert("There was an error adding the vehicle. Please try again.");
      setLoading(false);
    }
  };

  if (loading || loadingAuth) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Processing, please wait...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        marginBottom={4}
        marginTop={2}
      >
        <Typography variant="h4" align="center">
          Inventory
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancel" : "Add New Vehicle"}
        </Button>
      </Box>

      {showForm && (
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Add New Vehicle
          </Typography>
          <Box component="form" onSubmit={handleFormSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Vehicle Type"
                  name="type"
                  value={newVehicle.type}
                  onChange={handleInputChange}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="Motorbike">Motorbike</option>
                  <option value="Car">Car</option>
                </TextField>
              </Grid>

              {/* Plate Number Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Plate Number"
                  name="plateNr"
                  value={newVehicle.plateNr}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              {/* Brand Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Brand"
                  name="brand"
                  value={newVehicle.brand}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              {/* Model Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Model"
                  name="model"
                  value={newVehicle.model}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={newVehicle.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              {/* Pricing Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1">Pricing</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      required
                      label="Base Daily Rate"
                      name="dailyRate"
                      value={newVehicle.dailyRate}
                      onChange={handleInputChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Rate for 1 Week+ (optional)"
                      name="oneWeekRate"
                      value={newVehicle.oneWeekRate}
                      onChange={handleInputChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Rate for 2 Weeks+ (optional)"
                      name="twoWeeksRate"
                      value={newVehicle.twoWeeksRate}
                      onChange={handleInputChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Rate for 1 Month+ (optional)"
                      name="oneMonthRate"
                      value={newVehicle.oneMonthRate}
                      onChange={handleInputChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      required
                      label="Extra Hours Rate"
                      name="extraHoursRate"
                      value={newVehicle.extraHoursRate}
                      onChange={handleInputChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              {/* Deposit */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Deposit"
                  name="deposit"
                  value={newVehicle.deposit}
                  onChange={handleInputChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              {/* Additional Features */}
              <Grid item xs={12}>
                <Typography variant="subtitle1">Vehicle Features</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Engine Size (cc)"
                  name="engineSize"
                  value={newVehicle.engineSize}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fuel Type"
                  name="fuelType"
                  value={newVehicle.fuelType}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Number of Seats"
                  name="seats"
                  type="number"
                  value={newVehicle.seats}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Transmission"
                  name="transmission"
                  value={newVehicle.transmission}
                  onChange={handleInputChange}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="Automatic">Automatic</option>
                  <option value="Manual">Manual</option>
                </TextField>
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle1">Vehicle Image</Typography>
                <Button variant="contained" component="label">
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {imageFile && (
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    {imageFile.name} selected
                  </Typography>
                )}
              </Grid>
              {/* Extras Input */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Extras
                </Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      label="Extra Name"
                      value={extraName}
                      onChange={(e) => setExtraName(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      label="Price (leave empty for free)"
                      value={extraPrice}
                      onChange={(e) => setExtraPrice(e.target.value)}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ height: "100%" }}>
                      <Button
                        variant="outlined"
                        onClick={handleAddExtra}
                        fullWidth
                        sx={{ height: "100%" }}
                      >
                        Add
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                {newVehicle.extras.length > 0 && (
                  <Stack spacing={1} mt={2}>
                    {newVehicle.extras.map((extra, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography>
                          {extra.name} -{" "}
                          {extra.price === 0 ? "Free" : `₱${extra.price}`}
                        </Typography>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveExtra(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Grid>
              {/* Save Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  fullWidth
                >
                  Save Vehicle
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Inventory Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Vehicle</TableCell>
            <TableCell>Pricing</TableCell>
            <TableCell>Extras</TableCell>
            <TableCell>Image</TableCell>
            <TableCell>View</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingVehicles ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <CircularProgress />
                <Typography sx={{ mt: 1 }}>Loading vehicles...</Typography>
              </TableCell>
            </TableRow>
          ) : vehicles.length > 0 ? (
            vehicles.map((vehicle, index) => (
              <TableRow key={index}>
                {/* Status Column */}
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      color: vehicle.flags?.active ? "green" : "red",
                      fontWeight: "bold",
                    }}
                  >
                    {vehicle.flags?.active ? "Active" : "Inactive"}
                  </Typography>
                </TableCell>

                {/* Combined Vehicle Info Column */}
                <TableCell>
                  <Typography variant="subtitle2">{vehicle.plateNr}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {vehicle.brand} {vehicle.model}
                  </Typography>
                </TableCell>

                {/* Pricing Column */}
                <TableCell>
                  <Typography variant="body2">
                    <strong>Base:</strong> ₱
                    {Number(vehicle.pricing.daily).toFixed(0)}
                    {vehicle.pricing.oneWeek &&
                      ` | 1W: ₱${Number(vehicle.pricing.oneWeek).toFixed(0)}`}
                    {vehicle.pricing.twoWeeks &&
                      ` | 2W: ₱${Number(vehicle.pricing.twoWeeks).toFixed(0)}`}
                    {vehicle.pricing.oneMonth &&
                      ` | 1M: ₱${Number(vehicle.pricing.oneMonth).toFixed(0)}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {vehicle.pricing.extraHours &&
                      `Extra: ₱${Number(vehicle.pricing.extraHours).toFixed(
                        0
                      )}/h`}
                    {vehicle.deposit &&
                      ` | Deposit: ₱${Number(vehicle.deposit).toFixed(0)}`}
                  </Typography>
                </TableCell>

                {/* Extras */}
                <TableCell>
                  {vehicle.extras &&
                    vehicle.extras.map((extra, idx) => (
                      <div key={idx}>
                        {extra.name} -{" "}
                        {extra.price === 0 ? "Free" : `₱${extra.price}`}
                      </div>
                    ))}
                </TableCell>

                {/* Image */}
                <TableCell>
                  {vehicle.images && vehicle.images.length > 0 && (
                    <img
                      src={vehicle.images[0].url}
                      alt={vehicle.plateNr}
                      style={{ width: "100px" }}
                    />
                  )}
                </TableCell>

                {/* View Button */}
                <TableCell>
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/vehicle/${vehicle.id}`}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} align="center">
                No vehicles found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Container>
  );
};

export default Inventory;
